import {
    CircularProgress,
    InputAdornment,
    MenuItem,
    TextField,
} from '@mui/material';
import { FunctionComponent } from 'react';
import { WidgetVersion } from '@/models/widget';

interface WidgetVersionSelectProps {
    versions: WidgetVersion[];
    /** Empty until the version list resolves. */
    value: string;
    onChange: (version: string) => void;
    /** The version list is still being fetched. */
    isLoading?: boolean;
    /** The version list could not be fetched. */
    isError?: boolean;
}

/**
 * Picks the published widget version the preview loads and the snippet embeds.
 *
 * The field is always rendered, even while the version list is loading or after
 * it failed: choosing a version is the operator's job, so a missing control
 * would read as "this editor has no version concept" rather than "the list did
 * not load". A deprecated version stays selectable — it is still served — but
 * is flagged.
 *
 * Notices are the field's own `helperText` rather than an `Alert` beside it:
 * this sits in the widget card's header, where a block-level notice would push
 * the control past the rail's width. Retrying is the card's job — its error
 * state already offers it.
 */
export const WidgetVersionSelect: FunctionComponent<
    WidgetVersionSelectProps
> = ({ versions, value, onChange, isLoading, isError }) => {
    const isDeprecated = versions.some(
        ({ version, deprecated }) => version === value && deprecated
    );
    const hasVersions = versions.length > 0;

    const helperText = isError
        ? 'Versionen konnten nicht geladen werden.'
        : isDeprecated
          ? 'Veraltet, wird aber weiter ausgeliefert.'
          : undefined;

    return (
        <TextField
            select
            fullWidth
            label='Version'
            /*
             * MUI reads an empty string as "nothing selected"; `undefined`
             * would make this an uncontrolled input and warn on the first
             * successful load.
             */
            value={hasVersions ? value : ''}
            disabled={!hasVersions}
            error={isError}
            helperText={helperText}
            onChange={(event) => onChange(event.target.value)}
            InputProps={
                isLoading
                    ? {
                          startAdornment: (
                              <InputAdornment position='start'>
                                  <CircularProgress size={14} />
                              </InputAdornment>
                          ),
                      }
                    : undefined
            }
        >
            {versions.map(({ version, deprecated }) => (
                <MenuItem key={version} value={version}>
                    {deprecated ? `${version} (veraltet)` : version}
                </MenuItem>
            ))}
        </TextField>
    );
};
